import { useReveal } from "@/hooks/useReveal";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const STAGES = [
  {
    code: "01",
    label: "Signal",
    q: "What changed?",
    desc: "The earliest detectable shift in operating behavior, before it has been classified as a problem.",
  },
  {
    code: "02",
    label: "Friction",
    q: "Where is the system slowing down?",
    desc: "The point at which the network begins to absorb the signal as latency, exception, or operational drag.",
  },
  {
    code: "03",
    label: "Cause",
    q: "What decision, constraint, or behavior created it?",
    desc: "The underlying decision logic — planning rule, vendor schedule, lane design — that produced the friction.",
  },
  {
    code: "04",
    label: "Cost",
    q: "Where does the financial consequence appear?",
    desc: "The point at which the system exports the friction as expedited freight, dwell, or service failure.",
  },
  {
    code: "05",
    label: "Control",
    q: "What must be redesigned?",
    desc: "The decision, system, or boundary that must change so the signal does not return as a cost.",
  },
];

export default function FrameworkSection() {
  const { ref, visible } = useReveal();
  const navigate = useNavigate();

  return (
    <section
      id="framework"
      ref={ref}
      data-testid="framework-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-5">
              <span className="num-label">SECTION 04 / THE FRAMEWORK</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <h2
              data-testid="framework-headline"
              className={`font-display font-bold text-axiom-text tracking-tight text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] reveal ${
                visible ? "in" : ""
              }`}
            >
              A Diagnostic Framework for Seeing the System{" "}
              <span className="text-axiom-muted">Beneath the Symptom.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p
              className={`text-axiom-muted text-base sm:text-lg leading-relaxed reveal ${
                visible ? "in" : ""
              }`}
              style={{ transitionDelay: "80ms" }}
            >
              Most supply-chain problems are misread because they are examined
              at the point of pain rather than at the point of origin. The
              Axiom-AI Framework traces operational signals through friction,
              cause, cost, and control — so leaders can act before the network
              exports the problem somewhere else.
            </p>
          </div>
        </div>

        {/* Chain visualization */}
        <div className="mt-14 relative" data-testid="framework-chain">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-[44px] left-[5%] right-[5%] h-px bg-gradient-to-r from-transparent via-axiom-accent/50 to-transparent" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-2">
            {STAGES.map((s, i) => (
              <div
                key={s.code}
                data-testid={`framework-stage-${s.code}`}
                className={`relative axiom-card p-5 md:p-6 reveal ${
                  visible ? "in" : ""
                }`}
                style={{ transitionDelay: `${120 + i * 70}ms` }}
              >
                {/* Node circle on top */}
                <div className="hidden lg:flex absolute -top-[16px] left-1/2 -translate-x-1/2 w-8 h-8 items-center justify-center rounded-full bg-axiom-bg border border-axiom-accent/50">
                  <span className="signal-dot" style={{ width: 6, height: 6 }} />
                </div>

                <div className="flex items-center justify-between mb-4">
                  <span className="num-label">{s.code}</span>
                  <span className="num-label text-axiom-accent">STAGE</span>
                </div>
                <h3 className="font-display text-2xl text-axiom-text font-bold">
                  {s.label}
                </h3>
                <p className="mt-2 text-sm text-axiom-accent/90 font-medium">
                  {s.q}
                </p>
                <p className="mt-3 text-sm text-axiom-muted leading-relaxed">
                  {s.desc}
                </p>

                {/* Arrow to next */}
                {i < STAGES.length - 1 && (
                  <div className="lg:hidden flex items-center justify-center mt-4">
                    <ArrowRight size={14} className="text-axiom-accent" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer chain caption */}
          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 font-mono text-xs text-axiom-muted">
              <span>SIGNAL</span>
              <ArrowRight size={12} />
              <span>FRICTION</span>
              <ArrowRight size={12} />
              <span>CAUSE</span>
              <ArrowRight size={12} />
              <span>COST</span>
              <ArrowRight size={12} />
              <span className="text-axiom-accent">CONTROL</span>
            </div>
            <button
              data-testid="framework-cta"
              onClick={() => navigate("/contact")}
              className="btn-axiom-outline text-sm"
            >
              Explore the Framework
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
